import * as React from "react";
import { MutableRefObject } from "react";
import { useTranslation } from "react-i18next";

import Button from "components/button/Button";
import Import from "components/icons/Import";
import { subpageContext } from "components/layout/ApplicationLayout";
import { SubpageLayout } from "components/layout/subpage-layout/SubpageLayout";
import ImportLicenseKeyDialog, {
    LicenseKeyError,
} from "components/licenses/bulk-import-license-key/ImportLicenseKeyDialog";
import { OEM_LICENSE_ID } from "components/licenses/common";
import ViewImportedLicenseKeys, { SerialLicenseKey } from "components/licenses/ViewImportedLicenseKeys";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import Modal from "components/modal/Modal";
import { LICENSES_SERIAL_NUMBER_ROUTE } from "components/router/Routes";
import { LicenseData } from "domain/licenses";
import { licenseService } from "services/licenses/LicenseService";
import buttonsStyle from "styles/buttons.scss";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

interface Props {
    totalAvailableLicenses: number;
    availableLicenses: LicenseData[];
    oemLicenseExpirationDate?: string;
}
function BulkImportKeysView(props: Props): JSX.Element {
    const { t } = useTranslation();
    const [importVisible, setImportVisible] = React.useState(false);
    const [modalTitle, setModalTitle] = React.useState(t("Licenses.importKeysTitle"));
    const theme = useTheme();
    const context = React.useContext(subpageContext);
    const [oemLicenseKeys, setOemLicenseKeys] = React.useState<SerialLicenseKey[]>([]);
    const [oemFailedKeys, setOemFailedKeys] = React.useState<SerialLicenseKey[]>([]);
    const [oemLicenseKeysWithErrors, setOemLicenseKeysWithErrors] = React.useState<
        Map<string, LicenseKeyError> | undefined
    >(undefined);

    React.useEffect(() => {
        if (oemLicenseKeysWithErrors && oemLicenseKeysWithErrors.size > 0 && oemLicenseKeys.length > 0) {
            updateSubpage();
        }
    }, [oemLicenseKeysWithErrors, oemLicenseKeys]);
    const [assignLicensesCountFromCsv, setAssignLicensesCountFromCsv] = React.useState<number>(0);
    const [selectedFileListReference, setSelectedFileListReference] =
        React.useState<MutableRefObject<File[] | undefined>>();
    const [importModalVisible, setImportModalVisible] = React.useState(false);

    const onLicenseImportCallback = (
        oemLicenseKeys: SerialLicenseKey[],
        oemFailedKeys: SerialLicenseKey[],
        oemLicenseKeysWithErrors: Map<string, LicenseKeyError> | undefined,
        assignLicensesCountFromCsv: number,
        selectedFileListReference: MutableRefObject<File[] | undefined>
    ): void => {
        setOemLicenseKeys(oemLicenseKeys);
        setOemFailedKeys(oemFailedKeys);
        setOemLicenseKeysWithErrors(oemLicenseKeysWithErrors);
        setAssignLicensesCountFromCsv(assignLicensesCountFromCsv);
        setSelectedFileListReference(selectedFileListReference);
    };

    function fetchFailedKeys(message: string): Map<string, LicenseKeyError> | undefined {
        const match = message.match(/\[.*\]/);
        let arrayString = "";
        const licenseKeyHavingErrors = new Map<string, LicenseKeyError>();
        let keys: string[] = [];
        if (match) {
            arrayString = match[0].replace(/'/g, '"');
            keys = JSON.parse(arrayString);
        }

        if (keys.length > 0) {
            oemLicenseKeys.forEach((each) => {
                const existingError = licenseKeyHavingErrors.get(each.license_key) || {
                    licenseKey: each.license_key,
                    failingFields: [],
                };
                if (keys.includes(each.license_key)) {
                    existingError.failingFields.push(t("ViewOemKeys.table.licenseKeyExistsErrorMessageHighlight"));
                    licenseKeyHavingErrors.set(each.license_key, existingError);
                }
            });
            const failedKeys = oemLicenseKeys.filter((each) => keys.includes(each.license_key));
            setOemFailedKeys(failedKeys);
            return licenseKeyHavingErrors;
        }
        return undefined;
    }

    async function callImportBulkLicenseKeys(file: File, abortController: AbortController) {
        setImportModalVisible(true);
        await licenseService
            .assignBulkLicenseKeys(file, abortController)
            .then(() => {
                window.location.replace(LICENSES_SERIAL_NUMBER_ROUTE.path);
            })
            .catch((err) => {
                const licenseKeysWithErrors = fetchFailedKeys(err.toString());
                setImportModalVisible(false);
                setOemLicenseKeysWithErrors(licenseKeysWithErrors);
            });
    }

    const updateSubpage = () => {
        if (oemLicenseKeys.length <= 0) {
            return;
        }
        context?.setSubpage({
            content: (
                <SubpageLayout
                    visible={true}
                    title={t("ViewOemKeys.title")}
                    buttons={
                        <Button
                            variant={"PRIMARY"}
                            testId={testIds.workArea.license.serialNumber.importKeysView.importKeysButton}
                            onClick={() => {
                                if (oemFailedKeys.length > 0) {
                                    setImportVisible(true);
                                } else {
                                    if (oemLicenseKeys.length > 0) {
                                        const abortController = new AbortController();
                                        const file = selectedFileListReference?.current?.[0];
                                        if (!file) {
                                            return;
                                        }
                                        callImportBulkLicenseKeys(file, abortController);
                                    }
                                }
                            }}
                        >
                            {oemFailedKeys.length > 0
                                ? t("ImportOemSerialLicenseKeys.importOemSerialLicenseKeys.uploadAgain")
                                : t("Common.import")}
                        </Button>
                    }
                >
                    <ViewImportedLicenseKeys
                        validLicenseKeys={oemLicenseKeys}
                        failedLicenseKeys={oemFailedKeys}
                        availableLicensesCount={
                            (props.availableLicenses &&
                                props.availableLicenses.find((license) => license.type === OEM_LICENSE_ID)
                                    ?.available) ||
                            0
                        }
                        remainingLicensesCount={assignLicensesCountFromCsv}
                        licenseKeyCreationStatusDialog={false}
                        onViewImportClose={() => {}}
                        licenseKeysWithErrors={oemLicenseKeysWithErrors}
                        totalCsvLicenseKeysCount={oemLicenseKeys.length}
                        oemLicenseExpirationDate={props.oemLicenseExpirationDate}
                    />
                </SubpageLayout>
            ),
            isModal: true,
        });
    };

    function onHideImport() {
        setImportVisible(false);
        updateSubpage();
    }

    const bulkImportKeysText = t("Licenses.bulkImportKeys");

    return (
        <>
            {!importModalVisible ? (
                <>
                    <div>
                        <button
                            className={buttonsStyle.secondaryButtonWithoutIcon}
                            disabled={importVisible}
                            onClick={() => setImportVisible(true)}
                            data-testid={testIds.workArea.license.serialNumber.uploadKeysButton}
                        >
                            <Import color={theme.iconFillColor} />
                            {bulkImportKeysText}
                        </button>
                    </div>
                    <Modal isOpen={importVisible} hideModal={onHideImport} modalTitle={modalTitle}>
                        <ImportLicenseKeyDialog
                            onClose={onHideImport}
                            totalAvailableLicenses={props.totalAvailableLicenses}
                            availableLicenses={props.availableLicenses}
                            onTitleChange={setModalTitle}
                            oemLicenseExpirationDate={props.oemLicenseExpirationDate}
                            onLicenseImportCallback={onLicenseImportCallback}
                        />
                    </Modal>
                </>
            ) : (
                <Modal
                    isOpen={importModalVisible}
                    hideModal={() => {}}
                    modalTitle={t("ImportOemSerialLicenseKeys.importOemSerialLicenseKeys.importingModalTitle")}
                    closeButton={false}
                >
                    <LoadingIndicator />
                </Modal>
            )}
        </>
    );
}

export default BulkImportKeysView;
